import { useEffect, useState } from "react";

/** "Is this a phone-shaped viewport?" (items 0093 and 0091)
 *
 *  Width alone is not enough. A phone held sideways is 844x390 -- wider than
 *  any width breakpoint would catch, and the case a visitor standing in front
 *  of a map actually hits -- so short viewports count too. A portrait totem
 *  (1080x1920) and a desktop browser are neither.
 *
 *  640px matches Tailwind's `sm`, so the classes and this hook agree about
 *  where the layout changes. */
const QUERY = "(max-width: 640px), (max-height: 520px)";

/** "Do the POI card and the directions panel both fit, side by side?" (item 0151)
 *
 *  Measured, not guessed. On desktop the card is `left-3 w-[340px]`, so it ends
 *  at 352px; the directions panel now starts at 364px and keeps a 68px gutter
 *  on the right for the floor pills (`right-3 w-11`). Below 820px the panel
 *  would be squeezed under ~390px -- narrower than the card it is standing
 *  next to, and too narrow for a step like "Gira a la derecha y avanza 3 m".
 *
 *  Above this width the two sit side by side. Below it the card yields to the
 *  route, exactly as it already does on a phone: the visitor asked for
 *  directions, so directions are the primary surface.
 *
 *  `compact` implies NOT this (both of its clauses fail one of these), so the
 *  phone behaviour is unchanged -- this only adds the middle band, 641px to
 *  819px, which had no rule at all and simply overlapped. */
const SIDE_BY_SIDE = "(min-width: 820px) and (min-height: 521px)";

/** "Does the menu dock at the TOP, with the map below it?" (item 0373)
 *
 *  A portrait screen up to 819px wide -- a phone held upright, a portrait
 *  tablet -- and anything 640px wide or less. The human, on the old bottom
 *  sheet: "I dont like this, menus should be at the top". There the search
 *  bar, the categories, the POI card and the directions stack down from the
 *  top edge, the map controls move to the bottom corners, and the camera is
 *  padded so the map centres in the part of the screen the panel leaves
 *  visible.
 *
 *  A portrait screen 820px wide or more -- the 1080x1920 Port Premia totem,
 *  and its 1080x1350 iframe inside the sitummap page -- does NOT dock
 *  (f0e189a): docked there, the menu became a band across the whole screen.
 *  It keeps the floating card top-left and the directions top-right, and
 *  ViewerPage frames a route around them (item 0377, `floatFit`).
 *
 *  Landscape keeps its own layouts: the floating card top-left on a wide
 *  screen, the 641-819px stacked band, and the bottom sheet on a phone held
 *  sideways (844x390), where a top panel would leave a strip of map a few
 *  dozen pixels tall. */
const TOP_DOCK = "(orientation: portrait) and (max-width: 819px), (max-width: 640px)";

const PORTRAIT = "(orientation: portrait)";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener("change", onChange);
    setMatches(mq.matches);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

export function useCompact() {
  return useMediaQuery(QUERY);
}

export function useSideBySide() {
  return useMediaQuery(SIDE_BY_SIDE);
}

export function useTopDock() {
  return useMediaQuery(TOP_DOCK);
}

/** Portrait (height >= width). With useSideBySide and not docked, this is the
 *  wide portrait screen whose route ViewerPage frames itself (item 0377). */
export function usePortrait() {
  return useMediaQuery(PORTRAIT);
}
